import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { GithubPicker } from 'react-color';
import {
  Avatar,
  Button,
  ClickAwayListener,
  List,
  ListItem, ListItemText, ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import NotificationsActive from '@material-ui/icons/NotificationsActive';

import * as actions from '../../../../state';
import styles from '../styles.module.css';

const messages = defineMessages({
  ALERTS: {
    id: 'ALERTS',
    defaultMessage: 'Alerts',
  },
  ADD_ALERT: {
    id: 'ADD_ALERT',
    defaultMessage: 'Add Alert',
  },
  NO_ALERTS: {
    id: 'NO_ALERTS',
    defaultMessage: 'No Alerts',
  },
  DEFAULT_COLOR: {
    id: 'DEFAULT_COLOR',
    defaultMessage: 'Default Color',
  },
  CHOOSE_DEFAULT_COLOR: {
    id: 'CHOOSE_DEFAULT_COLOR',
    defaultMessage: 'Choose default color',
  },
  EDIT_ALERT_TOOLTIP: {
    id: 'EDIT_ALERT_TOOLTIP',
    defaultMessage: 'Edit alert',
  },
  DELETE_ALERT_TOOLTIP: {
    id: 'DELETE_ALERT_TOOLTIP',
    defaultMessage: 'Remove alert',
  },
  MINUTES: {
    id: 'MINUTES',
    defaultMessage: 'Minutes',
  },
  SECONDS: {
    id: 'SECONDS',
    defaultMessage: 'Seconds',
  },
  AGENTS: {
    id: 'AGENTS',
    defaultMessage: 'Agents',
  },
  CALLS: {
    id: 'CALLS',
    defaultMessage: 'Calls',
  },
  PERCENT: {
    id: 'PERCENT',
    defaultMessage: 'Percent',
  },
  ABOVE: {
    id: 'ABOVE',
    defaultMessage: 'Above',
  },
  BELOW: {
    id: 'BELOW',
    defaultMessage: 'Below',
  },
});

const colors = [
  // eslint-disable-next-line max-len
  '#FFA727', '#FFCA28', '#FFEE58', '#D4E157', '#9CCC65', '#66BB6A', '#26A69A', '#26C6DA', '#29B6F6', '#42A5F5', '#5C6BC0', '#7D57C2', '#AB46BC', '#EC407A', '#EF5350', '#FFFFFF',
  // eslint-disable-next-line max-len
  '#FF9800', '#FFC106', '#FFEB3B', '#CDDC39', '#8BC34A', '#4CAF51', '#009688', '#01BCD4', '#03A9F4', '#2196F3', '#3F51B5', '#673AB7', '#9C27B0', '#E91E63', '#F44337', '#E0E0E0',
  // eslint-disable-next-line max-len
  '#FB8C00', '#FFB300', '#FDD835', '#C0CA33', '#7CB342', '#43A047', '#00897B', '#01ACC1', '#039BE5', '#1E88E5', '#3949AB', '#5E35B1', '#8E24AA', '#D81B60', '#E53935', '#9E9E9E',
  // eslint-disable-next-line max-len
  '#F57C00', '#FFA000', '#FBC02D', '#AFB42A', '#689F38', '#388E3C', '#00796B', '#0197A7', '#0288D1', '#1976D2', '#30409F', '#512DA8', '#7B1FA2', '#C2195B', '#D84315', '#616161',
  // eslint-disable-next-line max-len
  '#EF6C00', '#FF8F00', '#F9A825', '#9E9D24', '#558B2F', '#2E7D32', '#00695C', '#01838F', '#0277BD', '#1565C0', '#283593', '#4526A0', '#6A1B9A', '#AD1457', '#D32F2F', '#212121',
];

function DataSourceAlerts({
  addNewAlert,
  editAlert,
  chartHasSource,
  intl,
  selectAlert,
}) {
  const dispatch = useDispatch();

  const chartsManifest = useSelector(actions.readChartsManifest);
  const selectedChart = useSelector(actions.readStateSelectedChart);

  const [selectingColor, setSelectingColor] = useState(false);

  const handleColorSelect = (color) => {
    dispatch(actions.updateSelectedChart({
      ...selectedChart,
      properties: {
        ...selectedChart.properties,
        defaultColor: color.hex,
      },
      renderRequired: true,
    }));

    setSelectingColor(false);
  };

  const deleteAlert = (alert, aId) => {
    const chartDef = chartsManifest[selectedChart.type];

    if (chartDef.usesColumns) {
      selectedChart.data_sources[0].alerts.splice(aId, 1);
    } else {
      selectedChart.data_sources[alert.sourceIndex].alerts.splice(aId, 1);
    }

    selectAlert({
      sourceIndex: 0,
      direction: null,
      color: '#ff0000',
    });

    dispatch(actions.updateSelectedChart(selectedChart));
  };

  const toggleColorSelect = () => {
    setSelectingColor(!selectingColor);
  };

  const renderDefaultColor = () => {
    if (['ResponsiveStat', 'ResponsiveGauge'].includes(selectedChart.type) === false) { return; }

    let defaultColor;
    if (selectedChart.properties.defaultColor) {
      defaultColor = selectedChart.properties.defaultColor;
    } else if (selectedChart.type === 'ResponsiveStat') {
      defaultColor = '#E0E0E0';
    } else if (selectedChart.type === 'ResponsiveGauge') {
      defaultColor = 'green';
    }

    // eslint-disable-next-line consistent-return
    return (
      <ListItem
        key="alert_default"
        onClick={() => setSelectingColor(true)}
        className={styles.defaultColorListItem}
      >
        <Avatar className={styles.alertAvatar} style={{ backgroundColor: defaultColor }} />

        <ListItemText className={styles.listItemText} primary={<FormattedMessage {...messages.DEFAULT_COLOR} />} />

        <ListItemSecondaryAction className={styles.colorAction}>
          <IconButton className={styles.actionBtns} style={{ padding: 7, marginRight: -1 }}>
            <ArrowDropDown style={{ width: 28, height: 28 }} />
          </IconButton>
        </ListItemSecondaryAction>

        {selectingColor
          ? (
            <ClickAwayListener onClickAway={toggleColorSelect}>
              <GithubPicker
                display
                width="100%"
                triangle="hide"
                colors={colors}
                onChangeComplete={handleColorSelect}
              />
            </ClickAwayListener>
          )
          : ''}
      </ListItem>
    );
  };

  const renderAlert = (alert, aId) => {
    const chartDef = chartsManifest[selectedChart.type];

    let primaryLabel;
    let secondaryLabel;

    const alertDirectionStr = intl.formatMessage({ ...messages[alert.direction.toUpperCase()] });

    if (chartDef.usesColumns) {
      const tableSource = selectedChart.data_sources[0];
      const statDef = chartDef.dataOptions[tableSource.stat];
      const colDef = statDef.supportedColumns.find((column) => column.stat === alert.column);

      primaryLabel = colDef.label;

      if (colDef.unit === 'minutes') {
        const minutes = Math.floor(alert.threshold / 60);
        let seconds = alert.threshold % 60;
        if (seconds < 10) { seconds = `0${seconds}`; }
        secondaryLabel = `${alertDirectionStr} ${minutes}:${seconds}`;
      } else {
        const colDefUnit = intl.formatMessage({ ...messages[colDef.unit.toUpperCase()] });
        secondaryLabel = `${alertDirectionStr} ${alert.threshold} ${colDefUnit}`;
      }
    } else {
      const source = selectedChart.data_sources[alert.sourceIndex];

      if (source.stat === 'custom') {
        primaryLabel = 'custom';
        secondaryLabel = `${alertDirectionStr} ${alert.threshold}`;
      } else {
        const statDef = chartDef.dataOptions[source.stat];
        primaryLabel = statDef.label;
        if (statDef.unit === 'minutes') {
          const minutes = Math.floor(alert.threshold / 60);
          let seconds = alert.threshold % 60;
          if (seconds < 10) { seconds = `0${seconds}`; }
          secondaryLabel = `${alertDirectionStr} ${minutes}:${seconds}`;
        } else {
          const statDefUnit = intl.formatMessage({ ...messages[statDef.unit.toUpperCase()] });
          secondaryLabel = `${alertDirectionStr} ${alert.threshold} ${statDefUnit}`;
        }
      }
    }

    return (
      <ListItem key={`alert_${aId}`}>
        <Avatar className={styles.alertAvatar} style={{ backgroundColor: alert.color }}>
          {alert.bronoEnabled
            && <NotificationsActive style={{ width: 16, height: 16 }} />}
        </Avatar>

        <ListItemText className={styles.listItemText} primary={primaryLabel} secondary={secondaryLabel} />

        <ListItemSecondaryAction className={styles.deleteAction}>
          <Tooltip disableFocusListener title={<FormattedMessage {...messages.EDIT_ALERT_TOOLTIP} />}>
            <IconButton onClick={() => editAlert(alert, aId)} className={styles.actionBtns}>
              <EditIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip disableFocusListener title={<FormattedMessage {...messages.DELETE_ALERT_TOOLTIP} />}>
            <IconButton onClick={() => deleteAlert(alert, aId)} className={styles.actionBtns}>
              <DeleteIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <div className={styles.list} style={{ borderTop: '1px solid #eee' }}>
      <ListItem className={styles.sectionHeader}>
        <FormattedMessage {...messages.ALERTS} />
      </ListItem>

      <Button
        color="primary"
        onClick={addNewAlert}
        className={styles.addSourceBtns}
        disabled={!chartHasSource}
      >
        <AddIcon style={{ fontSize: 19, marginTop: -1 }} />
        <FormattedMessage {...messages.ADD_ALERT} />
      </Button>

      <List>
        {renderDefaultColor()}
        {selectedChart?.data_sources?.map((source) => {
          if (source.alerts && source.alerts.length) {
            return (source.alerts.map((alert, aId) => renderAlert(alert, aId)));
          }
          return '';
        })}
      </List>
    </div>
  );
}

export default injectIntl(DataSourceAlerts);
